<template>
  <div class="row wrapper border-bottom white-bg page-heading">
    <div class="col-lg-10">
      <h2>KSeF - dodaj certyfikat sprzedawcy</h2>
      <ol class="breadcrumb">
        <li>
          <router-link to="/">Home</router-link>
        </li>
        <li>
          <router-link to="/ksef/companies">KSeF - sprzedawcy</router-link>
        </li>
        <li>
          <router-link :to="`/ksef/companies/${routePk}`">
            {{ companyData.name || "-" }}
          </router-link>
        </li>
        <li class="active">
          <strong>Dodaj certyfikat sprzedawcy</strong>
        </li>
      </ol>
    </div>
  </div>

  <div class="wrapper wrapper-content animated fadeInRight">
    <form @submit.prevent="createCompanyCert" class="form-horizontal">
      <div class="row">
        <div class="col-lg-12">
          <div class="ibox float-e-margins">
            <div class="ibox-title">
              <h5>KSeF - dane certyfikatu sprzedawcy</h5>
              <span class="ibox-toggle" @click="isMainOpen = !isMainOpen">
                <i :class="chevronMainClass"></i>
              </span>
            </div>
            <div class="ibox-content" v-show="isMainOpen">
              <div class="has-error" v-if="serverErrors.non_field_errors">
                <span
                  class="help-block"
                  v-for="(error, index) in serverErrors.non_field_errors"
                  :key="index"
                >
                  {{ error }}
                </span>
              </div>

              <div class="form-group">
                <label class="control-label col-sm-2">Nazwa *</label>
                <div class="col-sm-10">
                  <input
                    type="text"
                    class="form-control inputClass"
                    v-model="certObj.label"
                    @input="cleanErrors('label')"
                    required
                  />
                  <div class="has-error" v-if="serverErrors.label">
                    <span
                      class="help-block"
                      v-for="(error, index) in serverErrors.label"
                      :key="index"
                    >
                      {{ error }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="control-label col-sm-2">Certyfikat *</label>
                <div class="col-sm-10">
                  <input
                    type="file"
                    class="form-control inputClass"
                    @change="setFile('cert_pem_file', $event)"
                    required
                  />
                  <div class="has-error" v-if="serverErrors.cert_pem_file">
                    <span
                      class="help-block"
                      v-for="(error, index) in serverErrors.cert_pem_file"
                      :key="index"
                    >
                      {{ error }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="control-label col-sm-2">Klucz prywatny *</label>
                <div class="col-sm-10">
                  <input
                    type="file"
                    class="form-control inputClass"
                    @change="setFile('key_pem_file', $event)"
                    required
                  />
                  <div class="has-error" v-if="serverErrors.key_pem_file">
                    <span
                      class="help-block"
                      v-for="(error, index) in serverErrors.key_pem_file"
                      :key="index"
                    >
                      {{ error }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="control-label col-sm-2">
                  Hasło klucza prywatnego *
                </label>
                <div class="col-sm-10">
                  <input
                    type="password"
                    class="form-control inputClass"
                    v-model="certObj.key_password"
                    @input="cleanErrors('key_password')"
                    required
                  />
                  <div class="has-error" v-if="serverErrors.key_password">
                    <span
                      class="help-block"
                      v-for="(error, index) in serverErrors.key_password"
                      :key="index"
                    >
                      {{ error }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <div class="col-sm-offset-2 col-sm-10">
                  <router-link
                    :to="`/ksef/companies/${routePk}`"
                    class="btn btn-white"
                  >
                    Anuluj
                  </router-link>
                  <button
                    v-if="canAddCert"
                    type="submit"
                    class="btn btn-primary"
                    :disabled="submitingForm"
                  >
                    Zapisz
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import router from "../../router/router";
import { hasPermission } from "../../permissions";
import { ksefCertificatePermissionsConst } from "../../constants/ksefConstants";

export default {
  name: "KsefCompanyCertCreate",
  setup() {
    const store = useStore();
    const route = useRoute();
    const routePk = ref(route.params.pk);
    const isMainOpen = ref(true);
    const submitingForm = ref(false);
    const companyData = ref({});
    const serverErrors = ref({});
    const certObj = ref({
      company: null,
      label: "",
      cert_pem_file: null,
      key_pem_file: null,
      key_password: "",
    });

    const chevronMainClass = computed(() =>
      isMainOpen.value ? "fa fa-chevron-up" : "fa fa-chevron-down"
    );

    const canAddCert = computed(() =>
      hasPermission(ksefCertificatePermissionsConst.add)
    );

    const ksefCompanyData = computed(() => store.getters.getKsefCompanyData);
    const createKsefCertificateData = computed(
      () => store.getters.getCreateKsefCertificateData
    );

    const getCompany = () => {
      store.dispatch("getKsefCompany", { pk: routePk.value });
    };

    const setFile = (key, event) => {
      certObj.value[key] = event.target.files?.[0] || null;
      cleanErrors(key);
    };

    const cleanErrors = (key) => {
      delete serverErrors.value[key];
    };

    const createCompanyCert = () => {
      if (!canAddCert.value) {
        return;
      }
      submitingForm.value = true;
      certObj.value.company = routePk.value;
      store.dispatch("createKsefCertificate", certObj.value);
    };

    watch(
      () => ksefCompanyData.value,
      function (newValue) {
        if (newValue && newValue.status === 200) {
          companyData.value = newValue.data;
        } else if (newValue && newValue.status === 404) {
          store.dispatch("showToaster", {
            type: "error",
            content: newValue.data?.detail || "Nie znaleziono firmy!",
          });
          router.push("/ksef/companies");
        }
      }
    );

    watch(
      () => createKsefCertificateData.value,
      function (newValue) {
        submitingForm.value = false;
        if (newValue && newValue.status === 201) {
          store.dispatch("showToaster", {
            type: "success",
            content: "Certyfikat został dodany!",
          });
          router.push(`/ksef/companies/${routePk.value}`);
        } else if (newValue && newValue.status) {
          serverErrors.value = newValue.data || {};
          var msg = [];
          if (newValue.data) {
            for (const [key, value] of Object.entries(newValue.data)) {
              msg.push(key + ": " + value);
            }
          }
          store.dispatch("showToaster", {
            type: "error",
            content:
              msg.length > 0
                ? msg[0]
                : "Wystąpił błąd podczas dodawania certyfikatu!",
          });
        }
      }
    );

    onMounted(() => {
      getCompany();
    });

    return {
      routePk,
      companyData,
      certObj,
      serverErrors,
      submitingForm,
      setFile,
      cleanErrors,
      createCompanyCert,
      isMainOpen,
      chevronMainClass,
      canAddCert,
    };
  },
};
</script>

<style scoped>
.ibox-title {
  position: relative;
}
.ibox-toggle {
  position: absolute;
  right: 15px;
  top: 12px;
  cursor: pointer;
}
</style>
